import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import MobileFrame from '../components/MobileFrame';
import { ShieldCheck, CreditCard } from "lucide-react";
import axios from 'axios';
import { data } from 'framer-motion/client';

const DownPayment: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const [searchParams] = useSearchParams();

    const [orderDetails, setOrderDetails] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [apiError, setApiError] = useState<string | null>(null);

    const orderId = searchParams.get('orderId');
    const transactionStatus = Number(searchParams.get('transactionStatus'));

    // Handle screen resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch order details
    useEffect(() => {
        if (!orderId) {
            setLoading(false);
            setApiError("Missing order ID");
            return;
        }

        const fetchOrderDetails = async () => {
            try {
                const response = await axios.get(
                    `${import.meta.env.VITE_API_BASE_URL}/gateway/order/${orderId}`
                );

                if (response.data?.success) {
                    setOrderDetails(response.data.data);
                } else {
                    throw new Error("Failed to fetch order details");
                }
            } catch (err: any) {
                setApiError(err.message || "Failed to fetch order details");
            } finally {
                setLoading(false);
            }
        };

        fetchOrderDetails();
    }, [orderId, transactionStatus]);

    // Payment handler
    const handlePayNow = async () => {
        if (!orderDetails) return;

        try {
            setIsLoading(true);
            setError("");

            const response = await axios.post(
                `${import.meta.env.VITE_API_BASE_URL}/easebuzz/payment`,
                {
                    orderId: orderDetails.orderId,

                   amount: parseFloat(orderDetails.partnerTotalDownPayment),
                 
                    productinfo: orderDetails.productInfo,

                    firstname: orderDetails?.customerName,

                    phone: orderDetails?.customerMobileNumber,

                    email: orderDetails?.customerEmail
                }
            );
            const data = response.data;

            if (data?.success && data?.paymentLink) {
                window.location.href = data.paymentLink;
            } else {
                setError(data?.message || "Payment initiation failed");
            }

        } catch (err) {
            console.error("Payment Error:", err);
            setError("Failed to initiate payment");
        } finally {
            setIsLoading(false);
        }
    };

    // Loading / Error UI
    if (loading) {
        return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }

    if (apiError) {
        return <div className="flex items-center justify-center h-screen text-red-500">{apiError}</div>;
    }

    const downPaymentAmount =
        orderDetails.partnerTotalDownPayment ;

    const DownPaymentContent = () => (

        <div className="flex flex-col h-full pt-14 md:pt-20 pb-6 px-4 bg-gradient-to-b from-blue-50 to-white dark:from-gray-900 dark:to-dark-bg">

            {/* Header */}
            <div className="mb-6 text-center">

                {/* Icon */}
                <div className="flex justify-center mb-2">
                    <div className="bg-blue-100 dark:bg-blue-900/30  rounded-full">
                        <CreditCard className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                    </div>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    You are almost there!
                </h1>

                <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                    Just one step away from confirming your order. Please complete the down payment to proceed.
                </p>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col items-center justify-center mb-4">

                <div className="w-full max-w-xs rounded-2xl p-[1px] bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg mb-8">

                    {/* Inner Card */}
                    <div className="bg-white dark:bg-dark-card rounded-2xl py-8 px-6 flex flex-col items-center relative overflow-hidden">

                        {/* Soft top glow */}
                        <div className="absolute top-0 left-0 w-full h-10 rounded-t-2xl bg-gradient-to-b from-blue-500/30 via-indigo-500/10 to-transparent blur-sm pointer-events-none" />

                        {/* Label (slightly higher) */}
                        <span className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-0">
                            Down Payment
                        </span>

                        {/* Amount (slightly lower) */}
                        <span className="text-4xl font-bold text-gray-900 dark:text-white mt-2 mb-3">
                            ₹{downPaymentAmount}
                        </span>

                        {/* Divider */}
                        <div className="relative w-20 h-[2px] mb-3 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-600 to-transparent bg-[length:200%_100%] animate-slide" />
                        </div>
                        {/* Subtext (slightly pushed down) */}
                        <span className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">
                            Pay this amount to proceed
                        </span>

                    </div>
                </div>
            </div>



            {/* Footer */}
            <div className="mt-auto">

                {/* Trust Note (more separated) */}
                <div className="flex items-center justify-center gap-2 mb-8 text-gray-500 dark:text-gray-400">
                    <ShieldCheck className="w-4 h-4 text-green-600" />
                    <span className="text-xs">Your payment is securely processed</span>
                </div>

                {/* Bottom Group (Logo + Button as one unit) */}
                <div className="flex flex-col items-center gap-3">

                    {/* Powered by */}
                    <img
                        src="/CleverPeLogos/PoweredBy.svg"
                        alt="Powered By CleverPe"
                        className="h-10 opacity-90"
                        onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/140x40?text=Powered+By+CleverPe';
                        }}
                    />

                    {/* Pay Button */}
                    <button
                        onClick={handlePayNow}
                        disabled={isLoading || !orderDetails}
                        className={`w-full py-3 text-lg font-semibold text-white rounded-full transition-all duration-200 ${isLoading
                            ? 'bg-gray-400 dark:bg-gray-700 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md active:scale-[0.97]'
                            }`}
                    >
                        {isLoading ? 'Processing Payment...' : 'Pay Now'}
                    </button>

                    {error && (
                        <p className="text-red-500 text-sm mt-3 text-center">{error}</p>
                    )}
                </div>
            </div>
        </div>
    );

    return isMobile ? (
        <div className="h-[100dvh] w-full p-5 overflow-hidden bg-white dark:bg-dark">
            <DownPaymentContent />
        </div>
    ) : (
        <MobileFrame>
            <DownPaymentContent />
        </MobileFrame>
    );
};

export default DownPayment;
